import { analyticsEvent } from '@app/packages/analytics/declaration'
import {
  postAnnouncementResponses,
  putAnnouncementResponsesById,
  putAnnouncementResponsesByIdCancel,
  putAnnouncementResponsesByIdDecline,
} from '@app/packages/api/v2/actions/announcement_responses'
import type { PostAnnouncementResponsesRequest, PutAnnouncementResponsesRequest } from '@app/packages/api/v2/types/params'
import { asError } from '@app/packages/asError/asError'

import { mergeAnnouncementResponseSlice, setAnnouncementResponseStatusSlice } from '@app/store/slices/announcement_responses'
import {
  addAnnouncementResponseSlice,
  failAnnouncementUpdateSlice,
  finishAnnouncementUpdateSlice,
  startAnnouncementUpdateSlice,
} from '@app/store/slices/announcements'
import { createThunk } from '@app/store/thunk'

import { getAnalyticsRouter } from './analytics'
import { callV2Api } from './api'

export const createAnnouncementResponse = (request: PostAnnouncementResponsesRequest) =>
  createThunk(async dispatch => {
    const { announcement_id } = request
    dispatch(startAnnouncementUpdateSlice(announcement_id))
    try {
      const resp = await dispatch(callV2Api(postAnnouncementResponses(request)))
      dispatch(addAnnouncementResponseSlice(announcement_id, resp.data.id))
      dispatch(finishAnnouncementUpdateSlice(announcement_id))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A sitter responded to an announcement. One of the conversions the funnel leads to.
          type: 'create_announcement_response',
          data: { announcement_id },
        })).set('google', 'yandex', 'amplitude', 'sentry')
      )
      return resp
    } catch (e) {
      dispatch(failAnnouncementUpdateSlice(announcement_id, asError(e)))
      dispatch(getAnalyticsRouter()).send(announcementResponseFailEvent(announcement_id))
      throw e
    }
  })

export const updateAnnouncementResponse = (response_id: string, announcement_id: string, request: PutAnnouncementResponsesRequest) =>
  createThunk(async dispatch => {
    try {
      const resp = await dispatch(callV2Api(putAnnouncementResponsesById(response_id, request)))
      dispatch(mergeAnnouncementResponseSlice(resp.data))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A sitter edited their response.
          type: 'update_announcement_response',
          data: { announcement_response_id: response_id, announcement_id },
        })).set('google', 'yandex', 'amplitude', 'sentry')
      )
      return resp
    } catch (e) {
      dispatch(getAnalyticsRouter()).send(announcementResponseFailEvent(announcement_id, response_id))
      throw e
    }
  })

export const cancelAnnouncementResponse = (response_id: string, announcement_id: string) =>
  createThunk(async dispatch => {
    dispatch(startAnnouncementUpdateSlice(announcement_id))
    try {
      const resp = await dispatch(callV2Api(putAnnouncementResponsesByIdCancel(response_id)))
      dispatch(setAnnouncementResponseStatusSlice(resp.data.id, 'canceled'))
      dispatch(finishAnnouncementUpdateSlice(announcement_id))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A sitter withdrew their response.
          type: 'cancel_announcement_response',
          data: { announcement_id, announcement_response_id: response_id },
        })).set('google', 'yandex', 'amplitude', 'sentry')
      )
      return resp
    } catch (e) {
      dispatch(failAnnouncementUpdateSlice(announcement_id, asError(e)))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A response could not be withdrawn.
          type: 'cancel_announcement_response_fail',
          data: { announcement_id, announcement_response_id: response_id },
        })).set('amplitude')
      )
      throw e
    }
  })

export const declineAnnouncementResponse = (announcement_id: string, response_id: string) =>
  createThunk(async dispatch => {
    try {
      const resp = await dispatch(callV2Api(putAnnouncementResponsesByIdDecline(response_id)))
      dispatch(setAnnouncementResponseStatusSlice(resp.data.id, 'declined'))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A parent declined a response.
          type: 'decline_announcement_response',
          data: { announcement_id, announcement_response_id: response_id },
        })).set('google', 'yandex', 'amplitude', 'sentry')
      )
      return resp
    } catch (e) {
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A response could not be declined.
          type: 'decline_announcement_response_fail',
          data: { announcement_id, announcement_response_id: response_id },
        })).set('amplitude')
      )
      throw e
    }
  })

/** Creating a response and editing one fail under the same name, so it is declared once. */
const announcementResponseFailEvent = (announcementId: string, responseId?: string) =>
  analyticsEvent(() => ({
    // A response could not be created, or an edit of one could not be saved.
    type: 'create_announcement_response_fail',
    data: { announcement_id: announcementId, response_id: responseId },
  })).set('amplitude')
